import React from 'react';
import { CornerSeat, useResizablePanel } from '@ofself/shell';
import { useLocation } from 'react-router-dom';
import { usePlace } from '../../nucleus/place';
import { useSpaceRole } from '../../nucleus/access';
import { GroupFaceIcon, PersonFace } from './faces';
import { useShelf, ShelfProvider, ViewSwitch } from './Shelf';
import { PageStrip, useIsPhone } from './PhoneNav';
import { FindBar } from './FacetRail';
import './NucleusPage.css';

// THE PAGE — the one frame every Nucleus page stands in:
//
//   · the header — one translucent strip on every page, the body scrolling
//       softly under it; the page's verbs float at its right: Find first, the
//       rest, the primary verb last and solid (the shell's corner array)
//   · the body   — the page's collection or form, on the desk; it scrolls under the header.
//       `notice` (Notice.js) stands at its top, above the page's search
//   · the right  — the inspector of whatever is open (`inspector`), its actions in its
//                  ActionBar; its head lines up with the header. `inspectorSeat`
//                  ({ initial, key }) gives a page that reads wide its own default width
//
// Nothing else on a page takes an action: not a hover button, not a tail cell.
export default function NucleusPage({ title, section, count, verbs, inspector, inspectorSeat, notice = null, children, className = '', bodyClassName = '' }) {
    const label = section ? `${section} · ${title}` : title;
    // the page's view (Cards | Rows), remembered per section; the switch stands
    // in the corner once the page holds a collection (Shelf.js)
    const place = usePlace();
    const phone = useIsPhone();
    const location = useLocation();
    const asked = new URLSearchParams(location.search).get('as');
    const shelf = useShelf(place.section?.id || 'home', asked);
    const choose = (v) => {
        shelf.setView(v);
        if (asked) { const q = new URLSearchParams(location.search); q.delete('as'); place.navigate(`${location.pathname}${q.toString() ? `?${q}` : ''}`, { replace: true }); }
    };
    // a page's find is the page's own, so it stands in the page, above the
    // content, never in the header (search everything is Home's, up there)
    const { finds, acts } = splitVerbs(verbs);
    const corner = (shelf.hasShelf || acts.length > 0) && (
        <div className="nucleus-float">
            {shelf.hasShelf && <ViewSwitch view={shelf.view} onChange={choose} />}
            {acts}
        </div>
    );
    return (
        <ShelfProvider value={shelf.value}>
        <div className={`nucleus-page ${className}`} aria-label={count != null ? `${label}, ${count}` : label} role="region">
            {corner && <CornerSeat>{corner}</CornerSeat>}
            <div className={`nucleus-page__split${inspector ? ' has-inspector' : ''}`}>
                <div className={`nucleus-page__body ${bodyClassName}`}>
                    {/* the header: a translucent strip the page's verbs float on (no
                        breadcrumb — the rail and the section column say where you are) */}
                    <header className="nucleus-page__head">
                        <SpaceLine />
                    </header>
                    {phone && <PageStrip pages={place.pages} active={place.page?.id} onSelect={place.go} />}
                    {/* what wants you on this page: above its search, the first thing read */}
                    {notice && <div className="nucleus-page__notice">{notice}</div>}
                    {/* a page's search: the same bar as a filtered page's, without Filters */}
                    {finds.map((f, i) => <div className="nx-pagefind" key={i}><FindBar value={f.props.value || ''} onChange={f.props.onChange} placeholder={f.props.placeholder} /></div>)}
                    {children}
                </div>
                {inspector && <InspectorSeat initial={inspectorSeat?.initial} seatKey={inspectorSeat?.key}>{inspector}</InspectorSeat>}
            </div>
        </div>
        </ShelfProvider>
    );
}

// THE INSPECTOR'S SEAT: the right column, its left edge dragged to any width
// (double-press hands it back; ←/→ on the focused edge nudge it). One width for
// every page, remembered on this device; the collection keeps at least its own.
// A page that reads wide (the graph's Nodes) passes its own `initial` and `key`,
// so its width is remembered apart from the rest.
const INSPECTOR_W = 384;
const inspectorMax = () => Math.max(320, Math.min(window.innerWidth * 0.6, window.innerWidth - 560));
function InspectorSeat({ children, initial = INSPECTOR_W, seatKey }) {
    const { width, isDragging, separatorProps } = useResizablePanel({
        initial, min: 320, max: inspectorMax, edge: 'left', storageKey: seatKey ? `nucleus:inspector-w:${seatKey}` : 'nucleus:inspector-w',
    });
    return (
        <div className={`nx-inspector-seat${isDragging ? ' is-dragging' : ''}`} style={{ '--nx-inspector-w': `${width}px` }}>
            <div className="nx-inspector-seat__edge" aria-label="Resize the details" {...separatorProps}><span aria-hidden="true" /></div>
            {children}
        </div>
    );
}

// WHICH SPACE, WHICH ROLE: a quiet line at the head of every page. In a group,
// the group's face and name, then Admin or Member, and a member's realm after
// it ("Book Club · Member · Editors"). In your own space, "Personal".
export function SpaceLine() {
    const { groupId, group, roleWord, realmName } = useSpaceRole();
    if (!groupId) {
        return (
            <p className="nucleus-page__space-line is-personal" aria-label="Personal">
                <PersonFace size={18} />
                <span className="nucleus-page__space-name">Personal</span>
            </p>
        );
    }
    const name = group?.name || 'This group';
    const label = [name, roleWord, roleWord && realmName].filter(Boolean).join(', ');
    return (
        <p className="nucleus-page__space-line" aria-label={label}>
            <GroupFaceIcon url={group?.avatar_url || group?.avatar || undefined} size={18} />
            <span className="nucleus-page__space-name">{name}</span>
            {roleWord && <span className="nucleus-page__space-role">{roleWord}</span>}
            {roleWord && realmName && <span className="nucleus-page__space-role nucleus-page__space-realm">{realmName}</span>}
        </p>
    );
}

// The verbs as given (an element, a fragment, falsy bits), flattened, with
// every FindField drawn out of them.
function splitVerbs(verbs) {
    const flat = [];
    const walk = (node) => React.Children.forEach(node, (c) => {
        if (!c) return;
        if (c.type === React.Fragment) walk(c.props.children);
        else flat.push(c);
    });
    walk(verbs);
    // drawn as a list, so each keeps the key it came with or takes its place
    const keyed = flat.map((c, i) => (c.key != null ? c : React.cloneElement(c, { key: `verb-${i}` })));
    return { finds: keyed.filter((c) => c.type === FindField), acts: keyed.filter((c) => c.type !== FindField) };
}

// A page verb: a glyph and a word, quiet until hovered (Kit.swift Verb).
export function Verb({ icon, children, onClick, active = false, danger = false, primary = false, disabled = false, title }) {
    const button = (
        <button
            type="button"
            className={`nucleus-verb${active ? ' is-active' : ''}${danger ? ' is-danger' : ''}${primary ? ' is-primary' : ''}`}
            onClick={onClick}
            disabled={disabled}
            title={title}
        >
            {icon && <span className="nucleus-verb__glyph" aria-hidden="true">{icon}</span>}
            {children && <span className="nucleus-verb__word">{children}</span>}
        </button>
    );
    // a disabled button takes no pointer, so its why would never show: a greyed
    // verb wears it on a cell around it (as the ActionBar does). The cell is
    // always there, so a verb that turns on or off stays the same element.
    return <span className="nucleus-verb-cell" title={disabled ? title : undefined}>{button}</span>;
}

// THE FIND — a page declares its search in `verbs`; NucleusPage draws it at the
// top of the page as the find bar (FacetRail.js), the same bar a filtered page
// has above its collection. Typing narrows the page (dims or hides, the page's call).
export function FindField() {
    return null;
}
